import React, { useEffect, useState } from 'react';
import { Input, Switch } from '@cokitchen/cokitchen-components';
import useDashboardContext from 'contexts/dashboard.context';
import useNewAddonContext from 'contexts/new-addon.context';
import { apiService } from 'services/base.service';
import { BrandType, MealType } from 'utils/types';
import { multiselectOptionsType } from 'pages/meals/convert-meal-category-to-addon-category';
import PageHeaderText from 'components/base/page-header-text';
import { mergeArrays } from 'utils/functions';
import { STORAGE_KEYS } from 'utils/constants';
import NewAddonCategoryModal from 'components/new-addon-category-modal';
import Select from 'components/base/select';
import { SelectItem } from 'components/base/select/types';

export type AddonType = { base_meal_id: string; default: boolean; name: string; amount: string };
const NewAddonDetails = () => {
  const data: any = localStorage.getItem(STORAGE_KEYS.NEW_ADDON_CATEGORY);
  const newCategoryData = data && JSON.parse(data);
  const { newAddonData, setNewAddonData, setIsValid } = useNewAddonContext();
  const { activeCokitchen, category } = useDashboardContext();
  const addonObject = { base_meal_id: '', default: false, name: '', amount: '' };
  const [addons, setAddons] = useState([...newAddonData.addons] as {
    base_meal_id: string;
    default: boolean;
    name: string;
    amount: string;
  }[]);
  const [brandsList, setBrandsList] = useState([] as multiselectOptionsType[]);
  const [brandsListLoading, setBrandsListLoading] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');
  const [parentMealList, setParentMealList] = useState([] as any);
  const [parentMealSearch, setParentMealSearch] = useState('');
  const [parentMealLoading, setParentMealLoading] = useState(false);
  const [addonCategoryList, setAddonCategoryList] = useState([] as any);
  const [addonCategorySearch, setAddonCategorySearch] = useState('');
  const [addonCategoryLoading, setAddonCategoryLoading] = useState(false);
  const [baseMealSearch, setBaseMealSearch] = useState('');
  const [baseMealsLoading, setBaseMealsLoading] = useState(false);
  const [baseMealOptions, setBaseMealOptions] = useState<(SelectItem & { amount: string })[]>([]);
  const [isChecked, setIsChecked] = useState(false);

  const fetchBrandData = async () => {
    setBrandsListLoading(true);
    try {
      const params = { cokitchen_id: activeCokitchen.id, search: brandSearch };
      const res = await apiService.get('brand_management/brands', { params });
      const newList = res.data
        ?.filter((b: BrandType) => b.item_type === category.toUpperCase() && !b.archived)
        .map((brand: BrandType) => ({
          text: brand?.name,
          value: brand?.id
        }));
      setBrandsList(mergeArrays(brandsList, newList, 'value'));
    } catch {
      setBrandsList(newCategoryData ? [newCategoryData.brand] : []);
    } finally {
      setBrandsListLoading(false);
    }
  };

  useEffect(() => {
    if (newCategoryData) {
      setNewAddonData({
        ...newAddonData,
        meal_addon_category_id: [newCategoryData.addon_category.value],
        brand_id: [newCategoryData.brand.value],
        parent_meal_id: [newCategoryData.meal.value]
      });
      setAddonCategoryList([...addonCategoryList, newCategoryData.addon_category]);
      setBrandsList([...brandsList, newCategoryData.brand]);
      setParentMealList([...parentMealList, newCategoryData.meal]);
      setTimeout(() => {
        localStorage.removeItem(STORAGE_KEYS.NEW_ADDON_CATEGORY);
      }, 100);
    }
  }, []);

  useEffect(() => {
    if (newAddonData?.addons?.length > 1) {
      setNewAddonData({ ...newAddonData, addons: addons });
    }
  }, [addons]);

  useEffect(() => {
    if (!category.includes('{')) fetchBrandData();
  }, [category, activeCokitchen, brandSearch]);

  const fetchParentMeals = async () => {
    try {
      setParentMealLoading(true);
      const data = { brand_ids: newAddonData.brand_id, search: parentMealSearch };
      const res = await apiService.post(`meals`, { data, isInternal: true });
      const meals = res.data.data;
      const newList = meals.map((parentMeal: any) => {
        return {
          value: parentMeal?.id,
          text: `${parentMeal?.brand?.name} - ${parentMeal?.name}`,
          category_id: parentMeal?.meal_category?.id
        };
      });
      setParentMealList(mergeArrays(parentMealList, newList, 'value'));
    } catch {
      setParentMealList([]);
    } finally {
      setParentMealLoading(false);
    }
  };

  const fetchAddonCategories = async () => {
    try {
      setAddonCategoryLoading(true);
      const data = { meal_ids: newAddonData?.parent_meal_id, search: addonCategorySearch };
      const res = await apiService.post(`internal/meal-addon-category`, { data, isInternal: true });
      const addonCategorys = res.data.data.map((item: any) => {
        return {
          value: item?.id,
          text: `${item?.meal.brand.name} - ${item?.base_addon_category.name}`
        };
      });
      setAddonCategoryList(mergeArrays(addonCategoryList, addonCategorys, 'value'));
    } catch {
      setAddonCategoryList([...addonCategoryList]);
    } finally {
      setAddonCategoryLoading(false);
    }
  };

  const fetchBaseMeals = async () => {
    try {
      setBaseMealsLoading(true);
      const params = { search: baseMealSearch };
      const res = await apiService.get('base/meals', { params });
      const newOptions = res.data.data.data.map((meal: MealType) => ({
        text: meal.name,
        value: meal.id,
        amount: meal.amount
      }));
      setBaseMealOptions(mergeArrays(baseMealOptions, newOptions, 'value'));
    } catch {
      setBaseMealOptions([...baseMealOptions]);
    } finally {
      setBaseMealsLoading(false);
    }
  };

  useEffect(() => {
    if (newAddonData?.parent_meal_id.length > 0) fetchAddonCategories();
  }, [newAddonData?.parent_meal_id, addonCategorySearch]);

  useEffect(() => {
    if (newAddonData?.brand_id?.length > 0) fetchParentMeals();
  }, [newAddonData.brand_id, parentMealSearch]);

  useEffect(() => {
    fetchBaseMeals();
  }, [baseMealSearch]);

  const validator =
    newAddonData.brand_id.length > 0 &&
    newAddonData.parent_meal_id.length > 0 &&
    newAddonData.meal_addon_category_id.length > 0 &&
    newAddonData.addons.length > 0;

  const handleChange = <T extends keyof AddonType>(
    field: T,
    value: AddonType[T],
    index: number
  ) => {
    const newAddons = [...addons];
    newAddons[index] = {
      ...newAddons[index],
      ...(field === 'base_meal_id'
        ? {
            [field]: value,
            name: baseMealOptions.find((item) => item.value === value)?.text || '',
            amount: baseMealOptions.find((item) => item.value === value)?.amount || ''
          }
        : { [field]: value })
    };
    setAddons(newAddons);
    setNewAddonData({ ...newAddonData, addons: [...newAddons] });
    setIsValid && setIsValid(validator);
  };

  const addAddon = () => {
    setAddons([...addons, addonObject]);
    setBaseMealSearch('');
  };

  const handleCloseAddonCategoryModal = (newAddonCategoryItem?: any) => {
    setIsChecked(false);
    if (newAddonCategoryItem) {
      setAddonCategoryList([...addonCategoryList, newAddonCategoryItem]);
      setNewAddonData({
        ...newAddonData,
        meal_addon_category_id: [
          ...newAddonData.meal_addon_category_id,
          newAddonCategoryItem?.value
        ]
      });
    }
  };

  return (
    <div>
      <PageHeaderText
        title='Create New Addon(s)'
        description='Ensure you provide correct details.'
      />
      <div className='mb-[24px]'>
        <p className='font-medium text-sm'>Select Brand(s)</p>
        <Select
          multiple
          hasSearch
          placeholder='Select brand(s)'
          values={newAddonData.brand_id}
          options={brandsList}
          loading={brandsListLoading}
          onChange={(e) => setNewAddonData({ ...newAddonData, brand_id: e })}
          onSearch={setBrandSearch}
        />
      </div>
      <div className='mb-[24px]'>
        <p className='font-medium text-sm'>Select Parent meal(s)</p>
        <Select
          multiple
          hasSearch
          placeholder='Select one or more meals'
          values={newAddonData.parent_meal_id}
          options={parentMealList}
          onChange={(e) => {
            setNewAddonData({ ...newAddonData, parent_meal_id: e });
          }}
          onSearch={setParentMealSearch}
          loading={parentMealLoading}
        />
      </div>
      <div className='mb-[24px]'>
        <p className='font-medium text-sm'>Select Addon Category(s)</p>
        <Select
          multiple
          hasSearch
          placeholder='Select addon Category(s)'
          values={newAddonData?.meal_addon_category_id}
          options={addonCategoryList}
          onChange={(k) => setNewAddonData({ ...newAddonData, meal_addon_category_id: k })}
          onSearch={setAddonCategorySearch}
          loading={addonCategoryLoading}
        />

        <div className='mt-8 bg-[#F9F6F6] rounded-md border-[0.5px] border-[#EEECEC] px-[20px] py-3'>
          <Input
            type='checkbox'
            className='mr-2'
            label='Add new addon category'
            checked={isChecked}
            onChange={() => {
              setIsChecked(!isChecked);
            }}
          />
        </div>

        {addons?.map((item, index) => (
          <div
            key={index}
            className='pt-[16px] pb-[24px] border border-[#EDEDEE] rounded-[8px] mt-6 '>
            <div className='border-b border-b-[#EDEDEE] mb-[12px] '>
              <p className='mx-[24px] mb-[12px] text-[14px] font-[500]'>Add-on meal</p>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-5 px-[24px]'>
              <div className='flex flex-col gap-1 mt-4'>
                <p className='text-sm font-medium'>Select base meal</p>
                <Select
                  hasSearch
                  placeholder='Select base meal'
                  value={item.base_meal_id}
                  options={baseMealOptions}
                  onChange={(id) => handleChange('base_meal_id', id, index)}
                  className='[&_.input-box]:!min-w-0'
                  onSearch={setBaseMealSearch}
                  loading={baseMealsLoading}
                />
              </div>

              <Input
                small
                label='Add-on name'
                type='text'
                name='name'
                placeholder='Jollof & Co'
                className='w-full mt-4 text-sm'
                inputClass='px-[20px] py-[14px]'
                value={item.name}
                onChange={(e) => handleChange('name', e.target.value, index)}
              />
              <Input
                small
                label='Add-on amount'
                type='text'
                name='amount'
                className='w-full mt-4 text-sm'
                inputClass='py-[14px]'
                placeholder='500'
                value={item.amount}
                onChange={(e) => handleChange('amount', e.target.value, index)}
                currency
              />
              <div className='w-1/2 flex flex-col gap-3 mt-4'>
                <p className='text-sm font-medium'>Set as default</p>
                <Switch
                  active={item.default}
                  onChange={(checked) => handleChange('default', checked, index)}
                />
              </div>
              <div className='col-span-full flex justify-end'>
                {index >= 1 && (
                  <button
                    className='text-[#E60A2B] text-[13px] font-[500]'
                    onClick={() =>
                      setAddons((prev: AddonType[]) =>
                        prev.filter((obj: any, i: number) => i !== index)
                      )
                    }>
                    - Remove addon
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        <button className='text-[#0080E5] text-[13px] font-[500] mt-2' onClick={addAddon}>
          + Add another addon
        </button>
      </div>

      {isChecked ? (
        <NewAddonCategoryModal
          isOpen={isChecked}
          onClose={handleCloseAddonCategoryModal}
          brands={brandsList}
          defaultBrands={newAddonData?.brand_id ?? []}
          meals={parentMealList}
          defaultParentMeals={parentMealList.filter((meal: any) =>
            newAddonData.parent_meal_id.includes(meal.value)
          )}
        />
      ) : null}
    </div>
  );
};

export default NewAddonDetails;
